import { ExamUsageStatus } from '@exam-management/graphql';
import { APP_PERMISSIONS } from '@exam-management/permissions';
import { Flex, type AlertProps, Divider } from '@shared/components';
import { usePermission } from '@shared/permissions';
import { useCallback, useMemo } from 'react';

import {
  BaseAlert,
  QSetCheckPublishedButton,
  QSetMarkExamUsageAsReadyButton,
  QSetMarkExamUsageAsInactiveButton,
  QSetDisablementButton,
} from '../../components';
import { usePublishedQSetListModal } from '../../providers';

function getAlertProps(params: {
  selectedQSetIds: string[];
  examUsageStatuses: (ExamUsageStatus | null)[];
  onCheckDuplicatePublished: () => void;
  onMarkAsReady: () => void;
  onMarkAsInactive: () => void;
  onDisable: () => void;
}): Partial<AlertProps> {
  const {
    selectedQSetIds,
    examUsageStatuses,
    onCheckDuplicatePublished,
    onMarkAsReady,
    onMarkAsInactive,
    onDisable,
  } = params;
  const { permissions } = usePermission();

  const selectedQSetCount = selectedQSetIds.length;
  const isQSetSelected = selectedQSetCount > 0;

  const areAllExamUsagesReady = examUsageStatuses?.every(
    (qsetExamUsageStatus) => qsetExamUsageStatus === ExamUsageStatus.Ready,
  );

  const shouldAllowMarkReady = examUsageStatuses?.every(
    (qSetExamUsageStatus) => qSetExamUsageStatus === ExamUsageStatus.Inactive,
  );

  const shouldAllowDisabled = examUsageStatuses?.every(
    (qSetExamUsageStatus) =>
      qSetExamUsageStatus &&
      [ExamUsageStatus.Inactive, ExamUsageStatus.Ready].includes(qSetExamUsageStatus),
  );

  const shouldAllowCheckDuplicatePublished = selectedQSetCount > 1;

  const actionMenus = useMemo(
    () => [
      {
        children: (
          <QSetCheckPublishedButton
            disabled={!shouldAllowCheckDuplicatePublished}
            onClick={onCheckDuplicatePublished}
          />
        ),
        requiredPermissions: [APP_PERMISSIONS.EXAM.QSET.VIEW],
      },
      {
        children: (
          <QSetMarkExamUsageAsReadyButton
            disabled={!shouldAllowMarkReady}
            onClick={onMarkAsReady}
          />
        ),
        requiredPermissions: [APP_PERMISSIONS.EXAM.QSET.UPDATE],
      },
      {
        children: (
          <QSetMarkExamUsageAsInactiveButton
            disabled={!areAllExamUsagesReady}
            onClick={onMarkAsInactive}
          />
        ),
        requiredPermissions: [APP_PERMISSIONS.EXAM.QSET.UPDATE],
      },
    ],
    [onMarkAsReady, onMarkAsInactive, shouldAllowMarkReady, areAllExamUsagesReady],
  );

  const permittedActionMenus = useMemo(
    () =>
      actionMenus.filter((menu) =>
        menu.requiredPermissions.every((permission) => permissions.includes(permission)),
      ),
    [permissions, actionMenus],
  );

  const statusMenus = useMemo(
    () => [
      {
        children: <QSetDisablementButton onClick={onDisable} disabled={!shouldAllowDisabled} />,
        requiredPermissions: [APP_PERMISSIONS.EXAM.QSET.STATUS],
      },
    ],
    [onDisable, shouldAllowDisabled],
  );

  const permittedStatusMenus = useMemo(
    () =>
      statusMenus.filter((menu) =>
        menu.requiredPermissions.every((permission) => permissions.includes(permission)),
      ),
    [permissions, statusMenus],
  );

  const showDivider = permittedActionMenus.length && permittedStatusMenus.length;

  if (!isQSetSelected) {
    return {
      children: 'เลือกชุดข้อสอบสำหรับจัดทดสอบ หรือตรวจสอบค่าความซ้ำ',
      severity: 'info',
      icon: undefined,
    };
  }

  return {
    children: `เลือก ${selectedQSetCount} รายการ`,
    severity: 'info',
    action: (
      <Flex sx={(theme) => ({ gap: theme.spacing(2) })}>
        {permittedActionMenus.map((menu) => menu.children)}
        {showDivider ? <Divider orientation='vertical' flexItem /> : null}
        {permittedStatusMenus.map((menu) => menu.children)}
      </Flex>
    ),
    icon: false,
  };
}

export default function PublishedQSetListBanner() {
  const {
    selectedQSetIds,
    examUsageStatuses,
    openDisableModal,
    openMarkAsInactiveModal,
    openMarkAsReadyModal,
    openCheckDuplicatePublishedModal,
  } = usePublishedQSetListModal();

  const handleMarkAsReady = useCallback(() => {
    openMarkAsReadyModal();
  }, [openMarkAsReadyModal]);

  const handleMarkAsInactive = useCallback(() => {
    openMarkAsInactiveModal();
  }, [openMarkAsInactiveModal]);

  const handleDisable = useCallback(() => {
    openDisableModal();
  }, [openDisableModal]);

  const handleCheckDuplicatePublished = useCallback(() => {
    openCheckDuplicatePublishedModal();
  }, [openCheckDuplicatePublishedModal]);

  return (
    <Flex sx={{ padding: (theme) => theme.spacing(4), width: '100%' }}>
      <BaseAlert
        {...getAlertProps({
          selectedQSetIds,
          examUsageStatuses,
          onMarkAsReady: handleMarkAsReady,
          onMarkAsInactive: handleMarkAsInactive,
          onCheckDuplicatePublished: handleCheckDuplicatePublished,
          onDisable: handleDisable,
        })}
        sx={{ minHeight: '48px' }}
      />
    </Flex>
  );
}
